export const DEFAULT_CHART_SAMPLING_CONFIG = {
  enabled: true,
  adaptiveBaseRangeDays: 30,
  deviceLegacyTimeDiffMultiplier: 12,
  dashboardLegacyTimeDiffMultiplier: 12,
  maxGeneratedPoints: 20000,
  maxPointsPerHourByRange: {
    upTo1Hour: 3600, upTo1Day: 600, upTo1Week: 120, upTo1Month: 30,
    upTo3Months: 10, upTo6Months: 5, over6Months: 2,
  },
  defaultPointsPerHourByRange: {
    upTo1Hour: 60, upTo1Day: 60, upTo1Week: 60, upTo1Month: 30,
    upTo3Months: 10, upTo6Months: 5, over6Months: 2,
  },
};

const toNumber = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const clampNumber = (value, fallback, min, max) => {
  const parsed = toNumber(value, fallback);
  return Math.min(max, Math.max(min, parsed));
};

export const normalizeChartSamplingConfig = (value = {}) => {
  const source = value && typeof value === 'object' ? value : {};
  const rawDeviceMultiplier = source.deviceLegacyTimeDiffMultiplier
    ?? source.legacyTimeDiffMultiplier
    ?? source.timeDiffMultiplier
    ?? source.multiplier;
  const rawDashboardMultiplier = source.dashboardLegacyTimeDiffMultiplier
    ?? source.legacyDashboardTimeDiffMultiplier
    ?? rawDeviceMultiplier;

  const maxPointsPerHourByRange = Object.fromEntries(
    Object.entries(DEFAULT_CHART_SAMPLING_CONFIG.maxPointsPerHourByRange).map(([key, fallback]) => [
      key, Math.round(clampNumber(source.maxPointsPerHourByRange?.[key], fallback, 1, 3600)),
    ])
  );
  const defaultPointsPerHourByRange = Object.fromEntries(
    Object.entries(DEFAULT_CHART_SAMPLING_CONFIG.defaultPointsPerHourByRange).map(([key, fallback]) => [
      key,
      Math.round(clampNumber(
        source.defaultPointsPerHourByRange?.[key],
        Math.min(fallback, maxPointsPerHourByRange[key]),
        1,
        maxPointsPerHourByRange[key]
      )),
    ])
  );

  return {
    enabled: source.enabled !== false,
    adaptiveBaseRangeDays: clampNumber(
      source.adaptiveBaseRangeDays ?? source.baseRangeDays,
      DEFAULT_CHART_SAMPLING_CONFIG.adaptiveBaseRangeDays,
      1,
      365
    ),
    deviceLegacyTimeDiffMultiplier: clampNumber(
      rawDeviceMultiplier,
      DEFAULT_CHART_SAMPLING_CONFIG.deviceLegacyTimeDiffMultiplier,
      1,
      240
    ),
    dashboardLegacyTimeDiffMultiplier: clampNumber(
      rawDashboardMultiplier,
      DEFAULT_CHART_SAMPLING_CONFIG.dashboardLegacyTimeDiffMultiplier,
      1,
      240
    ),
    maxGeneratedPoints: Math.round(clampNumber(
      source.maxGeneratedPoints,
      DEFAULT_CHART_SAMPLING_CONFIG.maxGeneratedPoints,
      100,
      100000
    )),
    maxPointsPerHourByRange,
    defaultPointsPerHourByRange,
  };
};

const estimateWindowSeconds = ({ rangeDays, config, mode = 'device' }) => {
  const normalized = normalizeChartSamplingConfig(config);
  const rangeSeconds = Math.max(1, Number(rangeDays) * 24 * 3600);
  const baseSeconds = Math.max(1, normalized.adaptiveBaseRangeDays * 24 * 3600);
  const effectiveSeconds = normalized.enabled ? Math.min(rangeSeconds, baseSeconds) : rangeSeconds;
  const multiplier = mode === 'dashboard'
    ? normalized.dashboardLegacyTimeDiffMultiplier
    : normalized.deviceLegacyTimeDiffMultiplier;
  const postMultiplier = mode === 'dashboard' ? 1 : 3;
  const windowSeconds = Math.max(1, Math.round((effectiveSeconds / 3600) * multiplier) * postMultiplier);
  const estimatedPoints = Math.ceil(rangeSeconds / windowSeconds);

  if (estimatedPoints > normalized.maxGeneratedPoints) {
    return Math.max(1, Math.ceil(rangeSeconds / normalized.maxGeneratedPoints));
  }

  return mode === 'dashboard' ? Math.max(10, windowSeconds) : windowSeconds;
};

export const estimateDefaultSamplingPoints = ({ rangeDays, config, mode = 'device' }) => {
  const rangeSeconds = Math.max(1, Number(rangeDays) * 24 * 3600);
  const windowSeconds = estimateWindowSeconds({ rangeDays, config, mode });
  return {
    windowSeconds,
    points: Math.ceil(rangeSeconds / windowSeconds),
  };
};

export const formatDuration = (seconds) => {
  const value = Number(seconds);
  if (!Number.isFinite(value) || value <= 0) return '-';
  if (value < 60) return `${Math.round(value)}s`;
  if (value < 3600) return `${Math.round(value / 60)}m`;
  if (value < 86400) return `${Number((value / 3600).toFixed(1))}h`;
  return `${Number((value / 86400).toFixed(1))}d`;
};
